import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { buildInbox } from "@/lib/inbox";
import { Page, PageHeader } from "@/components/Bits";
import { CheckCircle, CaretRight, ArrowsClockwise } from "@phosphor-icons/react";

export default function ActionInbox() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/overview");
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const items = buildInbox(data);

  const outstanding = items.filter((i) => i.count > 0);
  const total = outstanding.reduce((s, i) => s + i.count, 0);

  return (
    <Page>
      <PageHeader overline="Your workspace" title="Action Inbox"
        subtitle={data?.tenant_name ? `Everything waiting on you in ${data.tenant_name}.` : "Everything waiting on you, in one place."}>
        <button onClick={load} disabled={loading} data-testid="inbox-refresh-btn"
          className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:border-navy hover:bg-navy/5 transition-all disabled:opacity-60">
          <ArrowsClockwise size={16} /> Refresh
        </button>
      </PageHeader>

      {loading && !data && (
        <div className="py-16 text-center font-mono text-navy/65 text-sm tracking-widest">LOADING_DATA…</div>
      )}

      {data && total === 0 && !loading && (
        <div data-testid="inbox-all-clear"
          className="flex items-center gap-4 bg-positive/10 border border-positive/30 rounded-2xl px-6 py-8">
          <CheckCircle size={40} weight="fill" className="text-positive shrink-0" />
          <div>
            <div className="font-heading font-black text-navy text-xl">You're all caught up</div>
            <p className="text-sm text-navy/70 mt-1">Nothing needs your attention right now. Nice and tidy.</p>
          </div>
        </div>
      )}

      {data && total > 0 && (
        <div className="grid gap-3" data-testid="inbox-list">
          {outstanding.map((i) => {
            const Icon = i.icon;
            return (
              <button key={i.key} onClick={() => navigate(i.to)} data-testid={`inbox-item-${i.key}`}
                className="group flex items-center gap-4 text-left bg-white border border-slate-200 rounded-2xl px-5 py-4 shadow-card hover:border-orange hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-orange/10 text-orangeink flex items-center justify-center shrink-0">
                  <Icon size={22} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-navy">{i.label}</div>
                  <p className="text-sm text-navy/65 truncate">{i.desc}</p>
                </div>
                <span data-testid={`inbox-count-${i.key}`}
                  className="text-sm font-bold px-3 py-1 rounded-full bg-navy text-white tabular shrink-0">{i.count}</span>
                <CaretRight size={18} className="text-navy/30 group-hover:text-orange transition-colors shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {data && outstanding.length > 0 && outstanding.length < items.length && (
        <p className="text-xs text-navy/45 mt-6">Items with nothing outstanding are hidden. Total outstanding actions: <span className="font-semibold text-navy/70">{total}</span>.</p>
      )}
    </Page>
  );
}
